import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Divider,
  Flex,
  Link,
  Text,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";

import SearchPager from "../SearchPager";

function includesQ(text, q) {
  if (!q.trim()) return true;
  return String(text ?? "").toLowerCase().includes(q.toLowerCase());
}

function paginate(list, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { totalPages, safePage, pageItems: list.slice(start, start + pageSize) };
}

function CourseRow({ children }) {
  return (
    <Box
      borderRadius="14px"
      border="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
      p={4}
      bg={useColorModeValue("white", "gray.800")}
    >
      {children}
    </Box>
  );
}

export default function Teaching() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const pageBg = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const subtle = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const panelBg = useColorModeValue("white", "gray.800");

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
  };

  const PAGE_SIZE = 6;

  // Global search
  const [query, setQuery] = useState("");

  // Per-section pages (only relevant if a section list is long)
  const [pUO, setPUO] = useState(1);
  const [pClarkson, setPClarkson] = useState(1);

  useEffect(() => {
    setPUO(1);
    setPClarkson(1);
  }, [query]);

  // ===== Data (structured)
  const data = useMemo(() => {
    return {
      recentHighlight: {
        term: "2021 Winter to Present",
        courseCode: "ELG5142/CSI5342",
        courseTitle: "Ubiquitous Sensing for Smart Cities",
        syllabusLabel: "Tentative Syllabus",
        syllabusHref: "ELG5142-CSI5342__SyllabusW26.pdf",
        syllabusNote: "from previous year",
      },
      fall2020: {
        term: "2020 Fall",
        course: "ELG5255 Applied Machine Learning",
        note:
          "This course requires special interaction with student teams throughout the term. Audit mode participation is not possible.",
        syllabusLabel: "Tentative Syllabus",
        syllabusHref: "https://www.site.uottawa.ca/~bkantarc/ELG5255-SyllabusF20-.pdf",
      },
      uOttawa: [
        "CSI5140/ELG5142 Ubiquitous Sensing for Smart Cities",
        "CSI5140 Information & Communication Systems for Smart Cities (under Selected Topics in Computer Science)",
        "SEG4135 Cloud Systems and Networks",
        "CSI 3131 Operating Systems",
        "SEG4300 Topics in Software Engineering",
        "CEG2136 Computer Architecture I",
      ],
      clarkson: [
        "EE512/CS667 Cloud Systems and Networks",
        "EE491 / EE 693 Directed Study in Electrical and Computer Engineering (Independent Study)",
        "EE 408 Software Design for Visual Environments",
      ],
      itu: [
        {
          group: "Computer Engineering",
          courses: ["Introduction to Scientific and Engineering Computing (C/C++)", "Digital Signal Processing Lab"],
        },
        {
          group: "Information Technologies",
          courses: [
            "Computer and Network Security",
            "Computer Operating Systems (Advanced)",
            "Structured Programming (Java)",
            "Data Structures",
          ],
        },
      ],
    };
  }, []);

  // ===== Filtering (global)
  const searchActive = query.trim().length > 0;

  const recentMatch = useMemo(() => {
    const blob = [
      data.recentHighlight.term,
      data.recentHighlight.courseCode,
      data.recentHighlight.courseTitle,
      data.recentHighlight.syllabusLabel,
    ].join(" ");
    return includesQ(blob, query);
  }, [data, query]);

  const fall2020Match = useMemo(() => {
    const blob = [data.fall2020.term, data.fall2020.course, data.fall2020.note, data.fall2020.syllabusLabel].join(" ");
    return includesQ(blob, query);
  }, [data, query]);

  const uoFiltered = useMemo(() => data.uOttawa.filter((c) => includesQ(c, query)), [data, query]);
  const clarksonFiltered = useMemo(() => data.clarkson.filter((c) => includesQ(c, query)), [data, query]);

  const ituFiltered = useMemo(() => {
    if (!searchActive) return data.itu;
    return data.itu
      .map((g) => ({ ...g, courses: g.courses.filter((c) => includesQ(c, query)) }))
      .filter((g) => g.courses.length > 0 || includesQ(g.group, query));
  }, [data, query, searchActive]);

  const uoPg = useMemo(() => paginate(uoFiltered, pUO, PAGE_SIZE), [uoFiltered, pUO]);
  const clarksonPg = useMemo(() => paginate(clarksonFiltered, pClarkson, PAGE_SIZE), [clarksonFiltered, pClarkson]);

  // totals for header (approx counts)
  const totalItems =
    1 +
    1 +
    data.uOttawa.length +
    data.clarkson.length +
    data.itu.reduce((a, g) => a + g.courses.length, 0);

  const filteredItems =
    (recentMatch ? 1 : 0) +
    (fall2020Match ? 1 : 0) +
    uoFiltered.length +
    clarksonFiltered.length +
    ituFiltered.reduce((a, g) => a + g.courses.length, 0);

  // ===== UI helpers
  const Section = ({ title, children }) => (
    <Box
      border={`1px solid ${borderColor}`}
      borderRadius="20px"
      bg={panelBg}
      overflow="hidden"
      boxShadow={useColorModeValue("0 10px 25px rgba(0,0,0,0.05)", "0 10px 25px rgba(0,0,0,0.25)")}
    >
      <Box h="4px" bg={uoRed} opacity={0.85} />
      <Box p={{ base: 4, md: 6 }}>
        <Text fontSize="xl" fontWeight="800" mb={4}>
          {title}
        </Text>
        {children}
      </Box>
    </Box>
  );

  return (
    <Box bg={pageBg}>
      {/* Header */}
      <Container maxW="1100px" py={10}>
        <Text fontSize={{ base: "28px", md: "36px" }} fontWeight="900" textAlign="center">
          Teaching
        </Text>

        <Text textAlign="center" mt={2} fontSize="lg" color={textColor}>
          Teaching portfolio of{" "}
          <Link href="/" {...linkStyle}>
            Dr. Burak Kantarci
          </Link>
        </Text>

        <Divider mt={6} />
      </Container>

      <Container maxW="1100px" pb={14}>
        {/* Global search */}
        <SearchPager
          mode="search"
          title="Search in Teaching"
          query={query}
          onQueryChange={setQuery}
          page={1}
          pageSize={PAGE_SIZE}
          totalItems={totalItems}
          filteredItems={filteredItems}
          onPrev={() => {}}
          onNext={() => {}}
        />

        {/* Empty state */}
        {searchActive && filteredItems === 0 && (
          <Box mt={6} border={`1px solid ${borderColor}`} borderRadius="18px" p={6} bg={panelBg}>
            <Text color={subtle}>No results found for “{query}”.</Text>
          </Box>
        )}

        {/* Layout: two-column on desktop */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={6}>
          {/* Highlight: Graduate course */}
          {(!searchActive || recentMatch) && (
            <Section title={data.recentHighlight.term} >
              <CourseRow>
                <Text fontWeight="800" color={uoRed}>
                  Graduate course: {data.recentHighlight.courseCode}
                </Text>
                <Text mt={1} fontStyle="italic" color={textColor}>
                  {data.recentHighlight.courseTitle}
                </Text>

                <Flex mt={3} gap={2} wrap="wrap" align="center">
                  <Link href={data.recentHighlight.syllabusHref} isExternal {...linkStyle}>
                    {data.recentHighlight.syllabusLabel}
                  </Link>
                  <Text color={subtle} fontSize="sm">
                    [{data.recentHighlight.syllabusNote}]
                  </Text>
                </Flex>
              </CourseRow>
            </Section>
          )}

          {/* Fall 2020 */}
          {(!searchActive || fall2020Match) && (
            <Section title={data.fall2020.term}>
              <CourseRow>
                <Text fontWeight="800" color={textColor}>
                  {data.fall2020.course}
                </Text>
                <Text mt={2} color={subtle} lineHeight="1.7">
                  {data.fall2020.note}
                </Text>
                <Link href={data.fall2020.syllabusHref} isExternal {...linkStyle} display="inline-block" mt={3}>
                  {data.fall2020.syllabusLabel}
                </Link>
              </CourseRow>
            </Section>
          )}

          {/* uOttawa */}
          {(!searchActive || uoFiltered.length > 0) && (
            <Section title="University of Ottawa (since 2016)">

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {uoPg.pageItems.map((c, idx) => (
                  <CourseRow key={idx}>
                    <Text color={textColor}>{c}</Text>
                  </CourseRow>
                ))}
              </SimpleGrid>
            </Section>
          )}

          {/* Clarkson */}
          {(!searchActive || clarksonFiltered.length > 0) && (
            <Section title="Clarkson University (2014–2016)">

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {clarksonPg.pageItems.map((c, idx) => (
                  <CourseRow key={idx}>
                    <Text color={textColor}>{c}</Text>
                  </CourseRow>
                ))}
              </SimpleGrid>
            </Section>
          )}
        </SimpleGrid>

        {/* ITU full-width section */}
        {(!searchActive || ituFiltered.length > 0) && (
          <Box mt={6}>
            <Section title="ITU (before 2010)">
              {ituFiltered.map((group, idx) => (
                <Box key={idx} mt={idx === 0 ? 0 : 6}>
                  <Text fontWeight="900" color={textColor} mb={2}>
                    {group.group}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    {group.courses.map((c, j) => (
                      <CourseRow key={j}>
                        <Text color={textColor}>{c}</Text>
                      </CourseRow>
                    ))}
                  </SimpleGrid>
                </Box>
              ))}
            </Section>
          </Box>
        )}
      </Container>
    </Box>
  );
}